import * as React from "react";

// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  AssignTaxObligations,
  Dividends,
} from "@/components/tables/TableObligations";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Services
import { getDividends, getTaxSummary } from "@/services/tax-obligations";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
// import { ErrorResponse } from "@/types/ErrorResponse";
export const Route = createFileRoute("/_layout-private/report/obligation/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      active: search.active as Tab,
      startYear: Number(search.startYear) || 0,
      endYear: Number(search.endYear) || 0,
    };
  },
});
type Tab = "assignTaxObligations" | "dividends";
function RouteComponent() {
  const navigate = useNavigate();
  const {
    active,
    startYear: startYearParam,
    endYear: endYearParam,
  } = Route.useSearch();
  const id = String(localStorage.getItem("companyId"));
  const companyId = id.replace(/"/g, "");
  const initialZero = 0;
  const { delegationReport } = useAccessPermission();
  const [filter, setFilter] = React.useState({
    startYear: startYearParam || initialZero,
    endYear: endYearParam || initialZero,
  });

  const handleStartYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, startYear: yearValue }));

    if (filter.endYear !== initialZero && filter.endYear < yearValue) {
      setFilter((prev) => ({ ...prev, endYear: yearValue }));
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
          endYear: yearValue,
        }),
      } as NavigateOptions);
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          startYear: yearValue,
        }),
      } as NavigateOptions);
    }
  };

  const handleEndYearChange = (value: string) => {
    const yearValue = Number(value);
    setFilter((prev) => ({ ...prev, endYear: yearValue }));
    navigate({
      search: (prev) => ({
        ...prev,
        endYear: yearValue,
      }),
    } as NavigateOptions);
  };

  const { data: getTaxData, isLoading } = useQuery({
    queryKey: [
      "tax-obligations",
      companyId,
      active,
      filter.startYear,
      filter.endYear,
    ],
    queryFn: () =>
      getTaxSummary({
        companyId,
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
        skip: 0,
        limit: 100,
      }),
    enabled: !!companyId,
  });

  const { data: getDividendsData, isLoading: isLoadingDiv } = useQuery({
    queryKey: [
      "dividends",
      companyId,
      active,
      filter.startYear,
      filter.endYear,
    ],
    queryFn: () =>
      getDividends({
        companyId,
        startYear:
          filter.startYear === initialZero ? undefined : filter.startYear,
        endYear: filter.endYear === initialZero ? undefined : filter.endYear,
        skip: 0,
        limit: 100,
      }),
    enabled: !!companyId,
  });

  if (getTaxData || getDividendsData) {
    if (
      getTaxData?.response?.data?.message === "LOGGED_OUT" ||
      getDividendsData?.response?.data?.message === "LOGGED_OUT"
    ) {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  const handleTabChange = (value: Tab) => {
    console.log(value, "Tab");

    // Reset the filter state
    setFilter({
      startYear: initialZero,
      endYear: initialZero,
    });

    // Navigate with reset year values
    navigate({
      to: "/report/obligation",
      search: {
        active: value,
        startYear: initialZero,
        endYear: initialZero,
      },
    });
  };

  return delegationReport.isRead ? (
    <div>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານພັນທະອາກອນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານມອບພັນທະໃຫ້ລັດ</CardTitle>
          <CardDescription>ລາຍການແຜນມອບພັນທະໃຫ້ລັດ</CardDescription>
        </CardHeader>

        <CardContent>
          <Tabs
            value={active || "assignTaxObligations"}
            defaultValue={active || "assignTaxObligations"}
            onValueChange={(value) => handleTabChange(value as Tab)}
            className="flex flex-col gap-y-12"
          >
            <TabsList className="flex flex-wrap gap-2 justify-start items-start bg-transparent">
              <TabsTrigger
                value="assignTaxObligations"
                className="px-3 bg-gray-100 text-black data-[state=active]:bg-green-700 data-[state=active]:text-white"
              >
                ລາຍງານມອບພັນທະອາກອນ
              </TabsTrigger>
              <TabsTrigger
                value="dividends"
                className="px-3 bg-gray-100 text-black data-[state=active]:bg-green-700 data-[state=active]:text-white"
              >
                ລາຍງານມອບເງິນປັນຜົນ
              </TabsTrigger>
            </TabsList>

            <TabsContent value="assignTaxObligations">
              <AssignTaxObligations
                data={getTaxData}
                onStartYearChange={handleStartYearChange}
                onEndYearChange={handleEndYearChange}
                startYear={filter.startYear}
                endYear={filter.endYear}
                isloading={isLoading}
              />
            </TabsContent>
            <TabsContent value="dividends">
              <Dividends
                data={getDividendsData}
                onStartYearChange={handleStartYearChange}
                onEndYearChange={handleEndYearChange}
                startYear={filter.startYear}
                endYear={filter.endYear}
                isloading={isLoadingDiv}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  ) : (
    <NotPermission />
  );
}
